import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useLocation, useParams } from 'react-router-dom';
import { useGetStaffAllListQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useReturnFromICMutation } from '@/api/member-management/admissionRequest';
import AppTextField from '@/common/components/form-inputs/AppTextField';

interface Props {
	open: boolean;
	onClose: () => void;
}

const ReturnFromICDialog = (props: Props) => {
	const { open, onClose } = props;
	const { t } = useTranslation();
	const [returnFromIC] = useReturnFromICMutation();
	const { admissionRequestId } = useParams();
	const location = useLocation();
	const memberType = i18n.t(
		location.pathname.includes('admission-request-list')
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);

	const userInfo = useSelector(selectMyInfo);

	const { data: staffList }: any = useGetStaffAllListQuery(
		userInfo?.currentBranch?.id || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !userInfo?.currentBranch,
		}
	);
	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		actionRequiredBy: Yup.string().required(t('errorMessage.required')),
		reasonForReturn: Yup.string().required(t('errorMessage.required')),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset({});
		onClose();
	};
	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await returnFromIC({
			...data,
			id: admissionRequestId,
		});
		if ('data' in response) {
			enqueueSnackbar(
				t('snackbar.updateReturnFromICSuccess', { type: memberType }),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
				<DialogTitle variant='h5'>{t('button.returnFromIC')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.returnFromICText')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12}>
									<AppDropDownMenu
										control={methods.control}
										name='actionRequiredBy'
										label={t('member.responsibleStaff') + '*'}
										options={staffList?.data || []}
									/>
								</Grid>
								<Grid size={12}>
									<AppTextField
										control={methods.control}
										name='reasonForReturn'
										label={t('member.reasonForReturn') + '*'}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
		</Dialog>
	);
};

export default ReturnFromICDialog;
